import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ReportStatus,
  TimeframeInput,
  useGetOperationalHeroMetricsQuery,
} from 'src/graphql';
import { InsightsDashboard, State } from 'src/types';
import { convertMetricNameToId, getTestIdFromString } from 'src/utils';
import { buildLocationQueryVariables } from 'src/utils/insights/buildLocationQueryVariables';

interface Parameters {
  globalFilterSites: string[];
  globalFilterCountries: string[];
  selectedCaseCountryCodes: string[];
  selectedCaseRegionIds: string[];
  globalFilterTimeframe: TimeframeInput;
}

export const useHeroMetrics = ({
  globalFilterSites,
  globalFilterCountries,
  selectedCaseCountryCodes,
  selectedCaseRegionIds,
  globalFilterTimeframe,
}: Parameters) => {
  const storedGlobalFilters = useSelector(
    (state: State) =>
      state.insightsGlobalFilters[InsightsDashboard.OPERATIONAL],
  );
  const currentGlobalLocationFilter =
    storedGlobalFilters.currentGlobalLocationFilter;
  const [timeframe, setTimeframe] = useState(globalFilterTimeframe);
  const [selectedSites, setSelectedSites] = useState(globalFilterSites);
  const [selectedCountries, setSelectedCountries] = useState(
    globalFilterCountries,
  );
  const [selectedCaseCountries, setSelectedCaseCountries] = useState(
    selectedCaseCountryCodes,
  );
  const [selectedCaseRegions, setSelectedCaseRegions] = useState(
    selectedCaseRegionIds,
  );
  const queryVariables = buildLocationQueryVariables({
    locationFilter: currentGlobalLocationFilter,
    locationFilterCategory: storedGlobalFilters.locationFilterCategory,
    sites: selectedSites,
    countries: selectedCountries,
    caseCountryCodes: selectedCaseCountries,
    caseRegionIds: selectedCaseRegions,
  });
  const { data, isLoading } = useGetOperationalHeroMetricsQuery({
    firstStatus: ReportStatus.New,
    secondStatus: ReportStatus.Read,
    thirdStatus: ReportStatus.Investigating,
    timeframe,
    ...queryVariables,
  });

  const heroMetricData = [
    data?.getTimeToResolutionHeroMetric,
    data?.firstStatus,
    data?.secondStatus,
    data?.thirdStatus,
    data?.getCasesOver90DaysHeroMetric,
  ];

  useEffect(() => {
    setSelectedSites(globalFilterSites);
  }, [globalFilterSites]);

  useEffect(() => {
    setSelectedCountries(globalFilterCountries);
  }, [globalFilterCountries]);

  useEffect(() => {
    setSelectedCaseCountries(selectedCaseCountryCodes);
  }, [selectedCaseCountryCodes]);

  useEffect(() => {
    setSelectedCaseRegions(selectedCaseRegionIds);
  }, [selectedCaseRegionIds]);

  useEffect(() => {
    setTimeframe(globalFilterTimeframe);
  }, [globalFilterTimeframe]);

  return {
    isLoading,
    data: heroMetricData.filter(Boolean).map((metric) => ({
      id: convertMetricNameToId(metric?.category ?? ''),
      title: metric?.category ?? '',
      testId: getTestIdFromString(metric?.category ?? ''),
      metrics:
        metric?.subCategory?.map((s) => ({
          title: s.category,
          value: s.count === 0 ? '—' : Math.round(s.count),
        })) ?? [],
      tooltip: metric?.tooltip ?? '',
    })),
  };
};
